import { HttpClient } from "@angular/common/http";
import { Injectable, inject, signal, computed } from "@angular/core";
import { Router } from "@angular/router";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { Observable, switchMap, of, catchError, shareReplay, tap, map } from "rxjs";
import { serviceUrls } from "../config/app.config";
import { GENERIC_STATUS } from "../../shared/domain/common/generic-status";
import { AuthStatus, IUser, USER_GROUPS, UserRole } from "../models/user";

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly oidc = inject(OidcSecurityService);

  private readonly _status = signal<AuthStatus>('unknown');
  private readonly _user = signal<IUser | null>(null);
  private readonly _profileError = signal<unknown | null>(null);

  private bootstrapRequest$: Observable<void> | null = null;
  private loginStarted = false;

  readonly status = this._status.asReadonly();
  readonly user = this._user.asReadonly();
  readonly profileError = this._profileError.asReadonly();

  readonly isUnknown = computed(() => this._status() === 'unknown');
  readonly isCheckingAuth = computed(() => this._status() === 'checking-auth');
  readonly isLoadingProfile = computed(() => this._status() === 'loading-profile');
  readonly isAnonymous = computed(() => this._status() === 'anonymous');
  readonly isAuthenticated = computed(() => this._status() === 'authenticated');
  readonly hasProfileError = computed(() => this._status() === 'profile-error');

  readonly isResolving = computed(() =>
    this._status() === 'checking-auth' || this._status() === 'loading-profile'
  );

  readonly isReady = computed(() => this._status() === 'anonymous' || this._status() === 'authenticated' ||
    this._status() === 'profile-error'
  );

  readonly groups = computed(() => this._user()?.groups ?? []);

  readonly isAdmin = computed(() => this.groups().includes(USER_GROUPS.ADMIN));
  readonly isDriver = computed(() => this.groups().includes(USER_GROUPS.DRIVER) && !this.isAdmin());
  readonly isWarehouse = computed(() =>
    this.groups().includes(USER_GROUPS.WAREHOUSE_OPERATOR) && !this.isAdmin() && !this.isDriver()
  );
  readonly isUser = computed(() =>
    this.groups().includes(USER_GROUPS.USER) && !this.isAdmin() && !this.isDriver() && !this.isWarehouse()
  );

  bootstrapAuth(): Observable<void> {
    if (this.bootstrapRequest$) {
      return this.bootstrapRequest$;
    }

    this._status.set('checking-auth');

    this.bootstrapRequest$ = this.oidc.checkAuth().pipe(
      switchMap(({ isAuthenticated }) => {
        if (!isAuthenticated) {
          this._status.set('anonymous');
          this._user.set(null);
          this._profileError.set(null);
          return of(void 0);
        }

        this._status.set('loading-profile');
        return this.loadUserProfile();
      }),
      catchError((error) => {
        console.error('Errore durante checkAuth()', error);
        this._status.set('anonymous');
        this._user.set(null);
        this._profileError.set(null);
        this.loginStarted = false;
        return of(void 0);
      }),
      shareReplay(1)
    );

    return this.bootstrapRequest$;
  }

  private loadUserProfile(): Observable<void> {
    return this.http.get<{ user: IUser; groups: UserRole[] }>(`${serviceUrls.user}/user/getcurrentuser`).pipe(
      tap((response) => {
        this._user.set({ ...response.user, groups: response.groups, });
        const isUserActive = response.user.userStatus.idUserStatus === GENERIC_STATUS.ACTIVE.value;
        this._profileError.set(isUserActive ? null : 'user disabled');
        this._status.set(isUserActive ? 'authenticated' : 'profile-error');
        this.loginStarted = false;
      }),
      map(() => void 0),
      catchError((error) => {
        console.error('Errore caricamento profilo utente', error);
        this._user.set(null);
        this._profileError.set(error);
        this._status.set('profile-error');
        this.loginStarted = false;
        return of(void 0);
      })
    );
  }

  getHomeByRole(): string {
    if (this.isDriver()) return '/driver/shipments';
    if (this.isWarehouse()) return '/warehouse/deliveries';
    return '/deliveries';
  }

  startLogin(targetUrl?: string): void {
    if (this.loginStarted) {
      return;
    }

    this.loginStarted = true;
    if (targetUrl) {
      sessionStorage.setItem('postLoginTargetUrl', targetUrl);
    } else {
      sessionStorage.removeItem('postLoginTargetUrl');
    }
    this.oidc.authorize();
  }

  consumePostLoginTargetUrl(): string {
    const target = sessionStorage.getItem('postLoginTargetUrl');
    sessionStorage.removeItem('postLoginTargetUrl');
    return this.resolvePostLoginTargetUrl(target);
  }

  getPostLoginTargetUrl(): string {
    return sessionStorage.getItem('postLoginTargetUrl') || this.getHomeByRole();
  }

  private resolvePostLoginTargetUrl(target: string | null): string {
    const home = this.getHomeByRole();
    if (!target) return home;

    const normalizedTarget = target.trim();
    if (!normalizedTarget.startsWith('/')) return home;

    const blockedTargets = new Set(['/', '/dashboard', '/forbidden', '/profile-error']);
    return blockedTargets.has(normalizedTarget) ? home : normalizedTarget;
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const userRoles = this.groups();
    return roles.some(role => userRoles.includes(role));
  }

  goToProfileError(): Promise<boolean> {
    return this.router.navigateByUrl('/profile-error');
  }

  resetForDebug(): void {
    this.bootstrapRequest$ = null;
    this.loginStarted = false;
    this._status.set('unknown');
    this._user.set(null);
    this._profileError.set(null);
    sessionStorage.removeItem('postLoginTargetUrl');
  }
}
